import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, UserPlus, CreditCard, Phone, Coins, FileText } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { api, apiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export const SettingsTab = () => {
  const { settings, reload } = useSettings();
  const [contacts, setContacts] = useState(settings.contacts);
  const [rate, setRate] = useState(settings.default_exchange_rate);
  const [pms, setPms] = useState(settings.payment_methods || []);
  const [newPm, setNewPm] = useState({ bank: "", account_number: "", account_name: "", note: "" });
  const [admins, setAdmins] = useState([]);
  const [newAdmin, setNewAdmin] = useState({ name: "", email: "", password: "" });
  const [termsId, setTermsId] = useState("");
  const [termsEn, setTermsEn] = useState("");

  useEffect(() => {
    setContacts(settings.contacts);
    setRate(settings.default_exchange_rate);
    setPms(settings.payment_methods || []);
    setTermsId(settings.terms_id || "");
    setTermsEn(settings.terms_en || "");
  }, [settings]);

  const loadAdmins = () => api.get("/admin/users").then(({ data }) => setAdmins(data)).catch(() => {});
  useEffect(() => { loadAdmins(); }, []);

  const setC = (k, v) => setContacts((p) => ({ ...p, [k]: v }));
  const setPmField = (i, k, v) => setPms((p) => p.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));

  const saveContacts = async () => {
    try { await api.put("/admin/settings", { contacts }); toast.success("Contacts updated"); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveRate = async () => {
    try { await api.put("/admin/settings", { default_exchange_rate: Number(rate) }); toast.success("Exchange rate updated"); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveTerms = async () => {
    try { await api.put("/admin/settings", { terms_id: termsId, terms_en: termsEn }); toast.success("Terms & conditions updated"); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const addPm = async () => {
    if (!newPm.bank || !newPm.account_number) { toast.error("Bank and account number are required"); return; }
    try { await api.post("/admin/payment-methods", newPm); setNewPm({ bank: "", account_number: "", account_name: "", note: "" }); toast.success("Payment method added"); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const savePm = async (pm) => {
    try { await api.put(`/admin/payment-methods/${pm.id}`, pm); toast.success("Saved"); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const delPm = async (id) => {
    try { await api.delete(`/admin/payment-methods/${id}`); toast.success("Removed"); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const createAdmin = async () => {
    if (!newAdmin.email || newAdmin.password.length < 6) { toast.error("Email and a 6+ char password are required"); return; }
    try { await api.post("/admin/users", newAdmin); toast.success("Admin account created"); setNewAdmin({ name: "", email: "", password: "" }); loadAdmins(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4 mt-6">
      {/* Contacts */}
      <Card className="p-5 space-y-3">
        <h3 className="font-display font-semibold text-lg flex items-center gap-2"><Phone className="h-4 w-4 text-primary" /> Jastip Contacts</h3>
        <Field label="Managers"><Input data-testid="settings-managers" value={contacts.managers || ""} onChange={(e) => setC("managers", e.target.value)} /></Field>
        <Field label="WhatsApp (display)"><Input data-testid="settings-whatsapp" value={contacts.whatsapp || ""} onChange={(e) => setC("whatsapp", e.target.value)} /></Field>
        <Field label="WhatsApp (intl digits for wa.me)"><Input data-testid="settings-whatsapp-intl" value={contacts.whatsapp_intl || ""} onChange={(e) => setC("whatsapp_intl", e.target.value)} /></Field>
        <Field label="UK phone (no WhatsApp)"><Input data-testid="settings-ukphone" value={contacts.uk_phone || ""} onChange={(e) => setC("uk_phone", e.target.value)} /></Field>
        <Button data-testid="save-contacts-btn" onClick={saveContacts}><Save className="h-4 w-4 mr-1" /> Save contacts</Button>
      </Card>

      {/* Currency */}
      <Card className="p-5 space-y-3">
        <h3 className="font-display font-semibold text-lg flex items-center gap-2"><Coins className="h-4 w-4 text-primary" /> Currency Conversion</h3>
        <Field label="Exchange rate £1 → IDR (shown in header & used as default)">
          <Input data-testid="settings-rate" type="number" value={rate} onChange={(e) => setRate(e.target.value)} />
        </Field>
        <p className="text-xs text-muted-foreground">Each trip also keeps its own rate for fee conversion; this is the site-wide default shown in the header ticker.</p>
        <Button data-testid="save-rate-btn" onClick={saveRate}><Save className="h-4 w-4 mr-1" /> Save rate</Button>
      </Card>

      {/* Payment methods */}
      <Card className="p-5 space-y-3">
        <h3 className="font-display font-semibold text-lg flex items-center gap-2"><CreditCard className="h-4 w-4 text-primary" /> Payment Methods</h3>
        {pms.map((pm, i) => (
          <div key={pm.id} className="rounded-lg border border-border p-3 space-y-2" data-testid={`pm-${pm.id}`}>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Bank" data-testid={`pm-bank-${i}`} value={pm.bank} onChange={(e) => setPmField(i, "bank", e.target.value)} />
              <Input placeholder="Account number" data-testid={`pm-number-${i}`} value={pm.account_number} onChange={(e) => setPmField(i, "account_number", e.target.value)} />
              <Input placeholder="Account name" data-testid={`pm-name-${i}`} value={pm.account_name} onChange={(e) => setPmField(i, "account_name", e.target.value)} />
              <Input placeholder="Note (optional)" data-testid={`pm-note-${i}`} value={pm.note || ""} onChange={(e) => setPmField(i, "note", e.target.value)} />
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" data-testid={`pm-save-${pm.id}`} onClick={() => savePm(pm)}><Save className="h-3.5 w-3.5 mr-1" /> Save</Button>
              <Button size="sm" variant="ghost" data-testid={`pm-del-${pm.id}`} onClick={() => delPm(pm.id)}><Trash2 className="h-3.5 w-3.5 mr-1" /> Remove</Button>
            </div>
          </div>
        ))}
        <div className="rounded-lg border border-dashed border-border p-3 space-y-2">
          <p className="text-sm font-medium">Add new</p>
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Bank (e.g. BCA)" data-testid="pm-bank-new" value={newPm.bank} onChange={(e) => setNewPm((p) => ({ ...p, bank: e.target.value }))} />
            <Input placeholder="Account number" data-testid="pm-number-new" value={newPm.account_number} onChange={(e) => setNewPm((p) => ({ ...p, account_number: e.target.value }))} />
            <Input placeholder="Account name" data-testid="pm-name-new" value={newPm.account_name} onChange={(e) => setNewPm((p) => ({ ...p, account_name: e.target.value }))} />
            <Input placeholder="Note (optional)" data-testid="pm-note-new" value={newPm.note} onChange={(e) => setNewPm((p) => ({ ...p, note: e.target.value }))} />
          </div>
          <Button size="sm" data-testid="add-pm-btn" onClick={addPm}><Plus className="h-4 w-4 mr-1" /> Add payment method</Button>
        </div>
      </Card>

      {/* Admin accounts */}
      <Card className="p-5 space-y-3">
        <h3 className="font-display font-semibold text-lg flex items-center gap-2"><UserPlus className="h-4 w-4 text-primary" /> Admin Accounts</h3>
        <div className="space-y-1.5">
          {admins.map((a) => (
            <div key={a.id} className="flex items-center justify-between text-sm border-b border-border pb-1" data-testid={`admin-row-${a.email}`}>
              <span><b>{a.name}</b> · {a.email}</span>
              <span className="text-xs text-muted-foreground">{fmtDate(a.created_at, "en")}</span>
            </div>
          ))}
        </div>
        <div className="rounded-lg border border-dashed border-border p-3 space-y-2">
          <p className="text-sm font-medium">Create new admin account</p>
          <Input placeholder="Name" data-testid="admin-name" value={newAdmin.name} onChange={(e) => setNewAdmin((p) => ({ ...p, name: e.target.value }))} />
          <Input placeholder="Email" type="email" data-testid="admin-email" value={newAdmin.email} onChange={(e) => setNewAdmin((p) => ({ ...p, email: e.target.value }))} />
          <Input placeholder="Password (min 6 chars)" type="password" data-testid="admin-password" value={newAdmin.password} onChange={(e) => setNewAdmin((p) => ({ ...p, password: e.target.value }))} />
          <Button size="sm" data-testid="create-admin-btn" onClick={createAdmin}><UserPlus className="h-4 w-4 mr-1" /> Create account</Button>
        </div>
      </Card>

      <Card className="p-5 space-y-3 lg:col-span-2">
        <h3 className="font-display font-semibold text-lg flex items-center gap-2"><FileText className="h-4 w-4 text-primary" /> Terms &amp; Conditions</h3>
        <p className="text-xs text-muted-foreground">One rule per line. These are shown to customers in "View Terms &amp; Conditions" and on the booking page, in each language.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-sm">Bahasa Indonesia</Label>
            <Textarea data-testid="settings-terms-id" rows={7} value={termsId} onChange={(e) => setTermsId(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">English</Label>
            <Textarea data-testid="settings-terms-en" rows={7} value={termsEn} onChange={(e) => setTermsEn(e.target.value)} />
          </div>
        </div>
        <Button data-testid="save-terms-btn" onClick={saveTerms}><Save className="h-4 w-4 mr-1" /> Save terms</Button>
      </Card>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-sm">{label}</Label>{children}</div>
);
